import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicCacheService, PublicClass, PublicGalleryItem } from '../utils/publicCacheService';
import { contentService } from '../utils/contentService';
import { eventbriteService, EventbriteEvent } from '../utils/eventbriteService';
import SEO from '../components/SEO';

// Use public cache interfaces
type ClassSchedule = PublicClass
type GalleryItem = PublicGalleryItem

function SimpleHome() {
  const [classes, setClasses] = useState<ClassSchedule[]>([])
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([])
  const [heroContent, setHeroContent] = useState(contentService.getHeroContent())
  const [aboutContent, setAboutContent] = useState(contentService.getAboutContent())
  const [upcomingClasses, setUpcomingClasses] = useState<EventbriteEvent[]>([])
  const [eventbriteLoading, setEventbriteLoading] = useState(true)

  useEffect(() => {
    // Load from public cache
    loadActualData()

    // Load Eventbrite classes
    loadEventbriteClasses()

    // Listen for cache updates
    const unsubscribeCache = publicCacheService.subscribe('cache_updated', () => {
      console.log('SimpleHome: Cache updated, reloading...')
      loadActualData()
    })

    // Listen for server cache updates
    const handleServerCacheUpdate = () => {
      console.log('SimpleHome: Server cache updated, reloading...')
      loadActualData()
    }
    window.addEventListener('serverCacheUpdate', handleServerCacheUpdate)

    // Listen for content updates
    const unsubscribeContent = contentService.subscribe(() => {
      console.log('SimpleHome: Content updated, reloading...')
      setHeroContent(contentService.getHeroContent())
      setAboutContent(contentService.getAboutContent())
    })

    return () => {
      unsubscribeCache()
      unsubscribeContent()
      window.removeEventListener('serverCacheUpdate', handleServerCacheUpdate)
    }
  }, [])

  const loadEventbriteClasses = async () => {
    console.log('SimpleHome: Loading Eventbrite classes...')
    setEventbriteLoading(true)

    try {
      if (!eventbriteService.isConfigured()) {
        console.log('SimpleHome: Eventbrite not configured')
        setUpcomingClasses([])
        setEventbriteLoading(false)
        return
      }

      const events = await eventbriteService.getOrganizationEvents(50)
      console.log('SimpleHome: Loaded', events.length, 'events from Eventbrite')

      // Filter for upcoming events and sort by date
      const now = new Date()
      const upcoming = events
        .filter(event => new Date(event.start.local) >= now)
        .sort((a, b) => new Date(a.start.local).getTime() - new Date(b.start.local).getTime())
        .slice(0, 3) // Get next 3 upcoming

      console.log('SimpleHome: Showing', upcoming.length, 'upcoming classes')
      setUpcomingClasses(upcoming)
    } catch (error) {
      console.error('SimpleHome: Error loading Eventbrite classes:', error)
      setUpcomingClasses([])
    } finally {
      setEventbriteLoading(false)
    }
  }

  const loadActualData = async () => {
    // Load from server JSON files
    console.log('SimpleHome: Loading from server JSON files...')

    const classesData = await publicCacheService.getActiveClasses()
    console.log('SimpleHome: Loaded classes from server:', classesData.length)
    setClasses(classesData.slice(0, 2))

    const galleryData = await publicCacheService.getActiveGalleryItems()
    console.log('SimpleHome: Loaded gallery items from server:', galleryData.length)
    setGalleryItems(galleryData.slice(0, 2))
  }

  const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const handleEventbriteBooking = (eventId: string) => {
    const eventbriteUrl = eventbriteService.getEventUrl(eventId)
    window.open(eventbriteUrl, '_blank')
  }



  // LocalBusiness schema for homepage
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'The Salty Mermaid',
    description: 'Art Gallery & Studio in Ormond Beach, FL offering art classes, workshops, and creative supplies from 75+ local artists',
    image: 'https://saltymermaidart.com/social-image.jpg',
    url: 'https://saltymermaidart.com',
    telephone: '+1-904-704-7449',
    email: 'thesaltymermaidart@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1546 Ocean Shore Blvd',
      addressLocality: 'Ormond Beach',
      addressRegion: 'FL',
      postalCode: '32176',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '29.2858',
      longitude: '-81.0559'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '10:00',
        closes: '16:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/TheSaltyMermaidArt/'
    ]
  }

  return (
    <div>
      <SEO
        title="The Salty Mermaid - Art Gallery & Studio in Ormond Beach, FL"
        description="Join art classes and explore creative supplies at The Salty Mermaid. Browse artwork from 75+ local artists, book workshops, and unleash your creativity in Ormond Beach, Florida."
        canonicalUrl="https://saltymermaidart.com/"
        structuredData={localBusinessSchema}
      />
      {/* Enhanced Bubble System - 18 bubbles with varying sizes */}
      <div className="bubble bubble-1"></div>
      <div className="bubble bubble-2"></div>
      <div className="bubble bubble-3"></div>
      <div className="bubble bubble-4"></div>
      <div className="bubble bubble-5"></div>
      <div className="bubble bubble-6"></div>
      <div className="bubble bubble-7"></div>
      <div className="bubble bubble-8"></div>
      <div className="bubble bubble-9"></div>
      <div className="bubble bubble-10"></div>
      <div className="bubble bubble-11"></div>
      <div className="bubble bubble-12"></div>
      <div className="bubble bubble-13"></div>
      <div className="bubble bubble-14"></div>
      <div className="bubble bubble-15"></div>
      <div className="bubble bubble-16"></div>
      <div className="bubble bubble-17"></div>
      <div className="bubble bubble-18"></div>
      
      {/* Hero Section */}
      <section className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center text-white p-5 rounded" style={{backgroundColor: 'rgba(13, 110, 253, 0.3)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.2)'}}>
          <div className="mb-4">
            <img 
              src="/images/backgrounds/SM Logo.gif" 
              alt="The Salty Mermaid Logo" 
              className="img-fluid"
              style={{ maxHeight: '150px', maxWidth: '200px' }}
            />
          </div>
          <h2 className="display-4 fw-bold mb-4">{heroContent.title}</h2>
          <p className="h4 mb-5">Art Gallery & Studio</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <Link to="/classes" className="btn btn-primary btn-lg">
              🎨 Art Classes
            </Link>
          </div>
        </div>
      </section>
      
      {/* Featured Sections */}
      <section className="py-5">
        <div className="container">
          <div className="row g-4 justify-content-center">
            
            {/* Upcoming Classes from Eventbrite */}
            <div className="col-lg-8">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">
                    🎨 Upcoming Classes
                    <span className="badge bg-danger ms-2" style={{ fontSize: '0.6rem' }}>
                      <i className="fas fa-external-link-alt me-1"></i>Live
                    </span>
                  </h5>
                  {eventbriteLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="small text-muted mt-2">Loading from Eventbrite...</p>
                    </div>
                  ) : upcomingClasses.length > 0 ? upcomingClasses.map((event) => (
                    <div
                      key={event.id}
                      className="mb-3 d-flex cursor-pointer"
                      onClick={() => handleEventbriteBooking(event.id)}
                      style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div className="bg-light rounded me-3 d-flex align-items-center justify-content-center" style={{width: '60px', height: '60px', flexShrink: 0}}>
                        {event.logo?.url ? (
                          <img src={event.logo.url} alt={event.name.text} className="w-100 h-100 object-fit-cover rounded" />
                        ) : (
                          <span className="text-primary">🎨</span>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-primary mb-1" style={{ fontSize: '0.9rem' }}>
                          {event.name.text}
                        </h6>
                        <p className="small text-muted mb-1">
                          <i className="far fa-calendar me-1"></i>
                          {formatEventDate(event.start.local)}
                        </p>
                        {event.summary && (
                          <p className="small mb-1">{event.summary.substring(0, 60)}...</p>
                        )}
                        <button
                          className="btn btn-sm btn-outline-primary mt-1"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEventbriteBooking(event.id)
                          }}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>Book Now
                        </button>
                      </div>
                    </div>
                  )) : (
                    <p className="text-muted">No upcoming classes scheduled.</p>
                  )}
                  <Link to="/classes" className="btn btn-outline-primary btn-sm w-100 mt-2">
                    <i className="fas fa-calendar-alt me-2"></i>View All Classes
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-5 bg-white bg-opacity-75">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h3 className="display-5 fw-bold mb-4">Welcome to The Salty Mermaid</h3>
              <p className="h5 text-muted mb-4">
                {heroContent.subtitle}
              </p>
              <div className="row mt-5">
                <div className="col-md-3 mb-3">
                  <div className="text-primary">
                    <h2>75+</h2>
                    <p className="small">Local Artists</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-success">
                    <h2>7</h2>
                    <p className="small">Art Instructors</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div style={{color: '#dc3545'}}>
                    <h2>30+</h2>
                    <p className="small">Art Classes</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div style={{color: '#0c76a3'}}>
                    <h2>8</h2>
                    <p className="small">Years Serving</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-5 bg-primary bg-opacity-75 text-white">
        <div className="container text-center">
          <h3 className="mb-4">Ready to Start Your Artistic Journey?</h3>
          <p className="h5 mb-4">Join our community of artists and discover your creative potential!</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <Link to="/classes" className="btn btn-light btn-lg">
              Book a Class Today
            </Link>
            <Link to="/contact" className="btn btn-outline-light btn-lg">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SimpleHome;